<template>
  <div class="controller-card card-item" :class="{ 'controller-card-complete': isCompleteVariant }">
    <!-- 헤더: 모델명과 상태 배지 -->
    <div class="controller-card-header">
      <div class="controller-header-left">
        <slot name="headerLeftExtra" />
        <i :class="controllerIconClass" class="controller-icon"></i>
        <div class="controller-title-block">
          <span class="controller-model">{{ titleText }}</span>
          <span v-if="controller?._id" class="controller-serial">{{ controller._id }}</span>
          <div v-if="roomNameList.length > 0" class="controller-room-tags">
            <span
              v-for="name in roomNameList"
              :key="name"
              class="controller-room-tag"
            >{{ name }}</span>
          </div>
        </div>
      </div>

      <div class="controller-header-right">
        <slot name="headerRightExtra" />
      </div>
    </div>

    <!-- 본문: 컨트롤러 정보 -->
    <div class="controller-card-body">
      <!-- 버전 (default, update, complete variant에서만 표시) -->
      <div class="controller-info-item" v-if="showVersion && hasValidVersion">
        <div class="controller-info-line">
          <i class="fa-solid fa-code-branch me-2"></i>
          <span class="controller-info-label">펌웨어 버전:</span>
          <span class="controller-info-value">v{{ controller.version }}</span>
          <span
            v-if="badgeText && variant !== 'update'"
            class="status-badge status-badge-inline"
            :class="needsUpdate ? 'status-badge-update' : 'status-badge-current'"
          >
            <i
              v-if="needsUpdate"
              class="fa-solid fa-circle-exclamation me-1"
            ></i>
            <i
              v-else
              class="fa-regular fa-circle-check me-1"
            ></i>
            {{ badgeText }}
          </span>
        </div>
      </div>

      <!-- 업데이트 버전 비교 (update variant에서만 표시) -->
      <div class="controller-info-item" v-if="variant === 'update' && hasComparableVersions">
        <div class="controller-info-line">
          <i class="fa-solid fa-code-branch me-2"></i>
          <span class="controller-info-label">펌웨어 버전:</span>
          <div class="controller-version-group">
            <div class="version-item-inline">
              <span class="version-label-inline">현재</span>
              <span class="version-value-inline">v{{ controller.version }}</span>
            </div>
            <div class="version-arrow">
              <i class="fa-solid fa-arrow-right"></i>
            </div>
            <div class="version-item-inline">
              <span class="version-label-inline">최신</span>
              <span
                class="version-value-inline"
                :class="{ 'version-value-update': needsUpdate }"
              >
                v{{ controller.lastVersion }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <slot name="bodyExtra" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  controller: {
    type: Object,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  roomNames: {
    type: Array,
    default: () => [],
  },
  badgeMode: {
    // 'auto' | 'none'
    type: String,
    default: 'auto',
  },
  variant: {
    // 'default' | 'minimal' | 'withRooms' | 'update' | 'complete'
    type: String,
    default: 'default',
    validator: (value) => ['default', 'minimal', 'withRooms', 'update', 'complete'].includes(value),
  },
});

const titleText = computed(() => {
  return props.title?.length > 0 ? props.title : (props.controller?.model || '조명제어기');
});

const roomNameList = computed(() => {
  const list = Array.isArray(props.roomNames) ? props.roomNames : [];
  return list
    .map((n) => (typeof n === 'string' ? n : n?.name))
    .filter((n) => typeof n === 'string' && n.length > 0);
});

// 컨트롤러 타입 판단
const isTempController = computed(() => {
  if (!props.controller?._id) return false;
  return props.controller._id.split("_").length < 2;
});

// 컨트롤러 타입에 따른 아이콘 클래스
const controllerIconClass = computed(() => {
  return isTempController.value 
    ? 'fa-solid fa-thermometer-half' 
    : 'fa-regular fa-lightbulb';
});

// 유효한 버전인지 확인 (0, null, undefined가 아닌 경우)
const hasValidVersion = computed(() => {
  const version = props.controller?.version;
  return version !== undefined && version !== null && version !== '' && Number(version) > 0;
});

const hasComparableVersions = computed(() => {
  if (props.badgeMode !== 'auto') return false;
  
  const version = props.controller?.version;
  const lastVersion = props.controller?.lastVersion;
  
  // 버전이 유효하지 않으면 배지 표시 안 함
  if (!hasValidVersion.value) return false;
  
  // lastVersion도 유효해야 함
  return (
    lastVersion !== undefined &&
    lastVersion !== null &&
    lastVersion !== '' &&
    Number(lastVersion) > 0
  );
});

const needsUpdate = computed(() => {
  if (!hasComparableVersions.value) return false;
  return Number(props.controller.lastVersion) > Number(props.controller.version);
});

const badgeText = computed(() => {
  if (!hasComparableVersions.value) return '';
  return needsUpdate.value ? '업데이트 필요' : '최신 버전';
});

// variant에 따라 표시할 정보 제어
const showVersion = computed(() => {
  return ['default', 'update', 'complete'].includes(props.variant);
});

// complete variant일 때 완료 상태 강조
const isCompleteVariant = computed(() => {
  return props.variant === 'complete';
});
</script>

<style>
.controller-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  overflow: hidden;
}

.controller-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.controller-card-complete {
  border-color: #198754;
}

.controller-card-complete .controller-card-header {
  background-color: #d1e7dd;
  border-bottom-color: #198754;
}

/* 헤더 */
.controller-card-header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f8f9fa;
  gap: 0.75rem;
}

.controller-header-left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.controller-icon {
  color: #0d6efd;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.controller-title-block {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
  flex: 1;
}

.controller-model {
  font-size: 1rem;
  font-weight: 600;
  color: #212529;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.controller-serial {
  font-size: 0.8rem;
  font-weight: 500;
  color: #6c757d;
  line-height: 1.3;
  word-break: break-all;
}

.controller-room-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.controller-room-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.3;
  color: #0d6efd;
  background-color: #ffffff;
  border: 1px solid #0d6efd;
  border-radius: 0.35rem;
  white-space: nowrap;
}

.controller-header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  white-space: nowrap;
}

.status-badge-update {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
}

.status-badge-current {
  background: linear-gradient(135deg, #28a745 0%, #218838 100%);
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
}

/* 본문 */
.controller-card-body {
  padding: 1rem 1.25rem;
}

.controller-info-item {
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.controller-info-item:last-child {
  border-bottom: none;
}

.controller-info-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  overflow: hidden;
}

.controller-info-label {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  text-align: left;
}

.controller-info-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
  word-break: break-all;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.status-badge-inline {
  margin-left: 0.5rem;
  flex-shrink: 0;
}

/* 모바일 반응형 */
@media (max-width: 768px) {
  .controller-card-header {
    padding: 0.875rem 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .controller-card-body {
    padding: 0.875rem 1rem;
  }

  .controller-info-line {
    flex-wrap: nowrap;
    gap: 0.375rem;
    overflow: hidden;
  }

  .controller-info-label {
    font-size: 0.75rem;
    flex-shrink: 0;
  }

  .controller-info-value {
    font-size: 0.85rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
  }
}

/* 버전 그룹 스타일 (update variant용) */
.controller-version-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  white-space: nowrap;
  flex-shrink: 0;
}

.version-item-inline {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.version-label-inline {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
  white-space: nowrap;
}

.version-value-inline {
  font-size: 0.875rem;
  font-weight: 600;
  color: #212529;
  padding: 0.25rem 0.625rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  white-space: nowrap;
  min-width: fit-content;
}

.version-value-inline.version-value-update {
  background: linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%);
  color: #dc3545;
}

.version-arrow {
  color: #6c757d;
  font-size: 0.875rem;
  flex-shrink: 0;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .controller-version-group {
    flex-wrap: nowrap;
    gap: 0.375rem;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    flex: 1;
    min-width: 0;
  }

  .version-item-inline {
    font-size: 0.8rem;
  }

  .version-label-inline {
    font-size: 0.7rem;
  }

  .version-value-inline {
    font-size: 0.8rem;
    padding: 0.2rem 0.5rem;
  }

  .version-arrow {
    flex-shrink: 0;
  }
}
</style>
